import { configure as configureAnalytics, tracker } from '@ixigo/analytics';
import { configure } from '@ixigo/shell';
import { patchHistory } from '@repo/shared/navigation';
import { StartClient } from '@tanstack/react-start/client';
import { StrictMode } from 'react';
import { hydrateRoot } from 'react-dom/client';
import { syncGiftCardAppData } from '@/features/gift-cards/services/initialize-native-context';
import { cookieStore } from '@/utils/cookie';
import { getCommonHeaders, ixiUIDResolver } from '@/utils/headers';
import { getBrowserAuth } from './lib/login/auth';
import {
	hasNativeSessionChanged,
	hasSyncedNativeSession,
} from './lib/pwa/app-data';
import { getAppContext } from './utils/app-context';
import { initGlobalAppTracker } from './utils/track';

async function initEnvironment() {
	// Native gift-card launches can bypass /pwa/initialpage. Complete the
	// handoff before device discovery, auth snapshots, requests or analytics.
	await syncGiftCardAppData(cookieStore);
	const browserAuth = getBrowserAuth();
	patchHistory();
	const getIxiUID = ixiUIDResolver(cookieStore);

	// fire early to set the ixiUID cookie
	getIxiUID();

	const appContext = getAppContext();

	/**
	 * makes the fetch client available to the entire application
	 * with the given configuration
	 */
	configure({
		prefixUrl: import.meta.env.BASE_API_URL,
		hooks: {
			beforeRequest: async (request) => {
				await getIxiUID();
				// Auth header is owned by browserAuth.interceptor below, not set here
				// (getCommonHeaders no longer emits Authorization at all).
				// Native gift-card context wins when available; otherwise use the
				// same launch-URL/cookie resolution as the server.
				// `clientConfig()` re-reads cookies per call, so the ixiUID resolved
				// just above is picked up.
				const ctx = getCommonHeaders(appContext.clientConfig());
				if (ctx) {
					Object.entries(ctx).forEach(([k, v]) => request.headers.set(k, v));
				}
				// The login SDK owns the auth header (and skips auth-internal requests).
				browserAuth.interceptor.beforeRequest(request);
			},
			beforeError: async (httpError) => {
				// SDK clears its own session + emits auth-changed on a genuine 401;
				// no cookie-name knowledge or page reload here.
				browserAuth.interceptor.beforeError(httpError.response);
				return httpError;
			},
		},
	});
	if (hasSyncedNativeSession()) {
		// An unchanged native token can still have valid web refresh metadata.
		// Let the SDK restore that session before the synchronous route guard.
		await browserAuth.whenReady();
		cookieStore.syncCookies();
	}
	if (hasNativeSessionChanged()) {
		// Native may have handed off a different account. Invalidate the SDK's
		// persisted web-user cache through its public API, after transport setup.
		// AuthProvider joins the same in-flight lookup when it mounts.
		void browserAuth.refreshUser().catch(() => {});
	}

	configureAnalytics({
		clevertapProjectId: 'R5Z-849-WZ4Z',
		clientId: appContext.clientConfig().clientId,
		// TODO: fix this type issue in "@ixigo/analytics"
		cookie: cookieStore as Parameters<typeof configureAnalytics>[0]['cookie'],
	});
	tracker.onEvent(({ data, name }) => {
		if (import.meta.env.PUBLIC_APP_ENV !== 'prod') {
			// Group related console logs
			console.group(
				`%c🔍 Analytics Event: ${name}`,
				'color: #0770E4; font-weight: bold;',
			);
			// Log event properties in a table if they exist
			if (data) {
				console.table(data);
			}
			console.groupEnd();
		}
	});
	initGlobalAppTracker();
}

initEnvironment().then(() => {
	hydrateRoot(
		document,
		<StrictMode>
			<StartClient />
		</StrictMode>,
	);

	// bfcache can restore the page with stale visual state — e.g. Chrome's
	// own back-forward visual transitions may leave <html> visibility:hidden,
	// or app-level exit animations (page-exit class) may still be applied.
	// Force-clear these so the restored page is always visible.
	window.addEventListener('pageshow', (event) => {
		if (!event.persisted) return;
		document.documentElement.style.visibility = '';
		document.body.classList.remove('page-exit');
		document.getElementById('gateway-form')?.remove();
	});
});
